import { useEffect, useRef, useState } from "react";
import { FileText, Save, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { Field } from "@/components/bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OverTargetDialog, uploadFile } from "@/components/finalCheckBits";

const newClientId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const today = () => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10);

/** INPUT PROGRESS: Tanggal → Proyek → Vendor (punya SPK di proyek) → SPK vendor → Marking alokasi SPK → Qty (kosong) → PDF batch → Simpan.
 *  Qty = tambahan transaksi baru. Lebih target → dialog Yes/No + keterangan wajib. STB tetap per SPK dipakai/dibuat otomatis. */
export function ProgressInputPanel({ onSaved }) {
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ tanggal: today(), projectId: "", vendorId: "", spkId: "" });
  const [opts, setOpts] = useState({ vendors: [], spks: [], markings: [] });
  const [qtys, setQtys] = useState({});
  const [pdf, setPdf] = useState("");
  const [pdfName, setPdfName] = useState("");
  const [ket, setKet] = useState("");
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(null);
  const [overNotes, setOverNotes] = useState({});
  const [clientId, setClientId] = useState(newClientId());
  const [q, setQ] = useState("");
  const pdfRef = useRef(null);
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data.filter((p) => p.status !== "Dibatalkan"))).catch(() => {}); }, []);
  useEffect(() => {
    if (!f.projectId) { setOpts({ vendors: [], spks: [], markings: [] }); return; }
    api.get(`/progress/form?projectId=${f.projectId}&vendorId=${f.vendorId}&spkId=${f.spkId}`).then((r) => setOpts({ vendors: r.data.vendors || [], spks: r.data.spks || [], markings: r.data.markings || [] })).catch((e) => toast.error(fmtErr(e)));
  }, [f.projectId, f.vendorId, f.spkId]);

  const setProject = (v) => { setF({ ...f, projectId: v, vendorId: "", spkId: "" }); setQtys({}); };
  const setVendor = (v) => { setF({ ...f, vendorId: v, spkId: "" }); setQtys({}); };
  const setSpk = (v) => { setF({ ...f, spkId: v }); setQtys({}); };
  const marks = opts.markings.filter((m) => !q || m.code.toLowerCase().includes(q.toLowerCase()));
  const filled = opts.markings.filter((m) => Number(qtys[m.id]) > 0);
  const totPcs = filled.reduce((a, m) => a + Number(qtys[m.id]), 0);
  const totTon = filled.reduce((a, m) => a + Number(qtys[m.id]) * (m.unitTon || 0), 0);

  const onPdf = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("File harus PDF"); return; }
    try { setPdf(await uploadFile(file)); setPdfName(file.name); } catch (e) { toast.error(fmtErr(e)); }
  };
  const submit = async (notes) => {
    if (!f.spkId) { toast.error("Pilih Proyek → Vendor → SPK dulu"); return; }
    const rows = filled.map((m) => ({ markingId: m.id, qty: Number(qtys[m.id]), overNote: (notes || overNotes)[m.id] || "" }));
    if (!rows.length) { toast.error("Isi Qty progress (angka positif) minimal untuk 1 marking"); return; }
    if (rows.some((r) => !Number.isInteger(r.qty))) { toast.error("Qty harus bilangan bulat"); return; }
    if (!pdf) { toast.error("Upload PDF bukti progress wajib per batch"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/progress", { ...f, rows, pdfPath: pdf, keterangan: ket, clientId });
      toast.success(data.duplicate ? "Batch ini sudah tersimpan (pengiriman ganda diabaikan)" : `Progress tersimpan — ${rows.length} marking · ${totPcs} pcs → STB ${data.stbNo}`);
      setQtys({}); setPdf(""); setPdfName(""); setKet(""); setOver(null); setOverNotes({}); setClientId(newClientId());
      setF((x) => ({ ...x }));   // refresh info progress sebelumnya
      onSaved?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 422 && d?.code === "OVER_TARGET") { setOver(d.rows); return; }
      toast.error(fmtErr(e));
    } finally { setBusy(false); }
  };
  const sel = "border-slate-800 bg-slate-900/70";

  return (
    <div className="panel p-4" data-testid="progress-input-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="font-display text-base font-bold text-slate-100">Input Progress <span className="cn text-xs font-normal text-slate-500">进度录入</span></h3>
        <span className="text-[11px] text-slate-500">Qty progress yang di-input admin langsung menjadi realisasi STB, Delivery List, laporan & tagihan. Final Check MC dan QC dicatat terpisah.</span>
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <Field label="Tanggal"><Input type="date" value={f.tanggal} onChange={(e) => setF({ ...f, tanggal: e.target.value })} className={sel} data-testid="pi-tanggal" /></Field>
        <Field label="Proyek">
          <Select value={f.projectId} onValueChange={setProject}><SelectTrigger className={sel} data-testid="pi-project"><SelectValue placeholder="Pilih proyek" /></SelectTrigger>
            <SelectContent className="bg-[#0D1524] border-slate-800 max-h-64">{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.kode} · {p.nama}</SelectItem>)}</SelectContent></Select>
        </Field>
        <Field label="Vendor (punya SPK di proyek)">
          <Select value={f.vendorId} onValueChange={setVendor} disabled={!f.projectId}><SelectTrigger className={sel} data-testid="pi-vendor"><SelectValue placeholder={f.projectId ? (opts.vendors.length ? "Pilih vendor" : "Belum ada SPK di proyek ini") : "Pilih proyek dulu"} /></SelectTrigger>
            <SelectContent className="bg-[#0D1524] border-slate-800 max-h-64">{opts.vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.singkatan ? `${v.singkatan} · ` : ""}{v.nama}</SelectItem>)}</SelectContent></Select>
        </Field>
        <Field label="No. SPK">
          <Select value={f.spkId} onValueChange={setSpk} disabled={!f.vendorId}><SelectTrigger className={sel} data-testid="pi-spk"><SelectValue placeholder={f.vendorId ? "Pilih SPK" : "Pilih vendor dulu"} /></SelectTrigger>
            <SelectContent className="bg-[#0D1524] border-slate-800 max-h-64">{opts.spks.map((s) => <SelectItem key={s.id} value={s.id}>{s.no}</SelectItem>)}</SelectContent></Select>
        </Field>
      </div>

      {f.spkId && (
        <div className="mt-3">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-500">Marking alokasi SPK — isi Qty progress (tambahan) pada marking yang dikerjakan</span>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="cari marking…" className="ml-auto h-7 w-40 border-slate-800 bg-slate-900/70 text-xs" data-testid="pi-search" />
          </div>
          <div className="max-h-80 overflow-y-auto rounded border border-slate-800" data-testid="pi-marking-table">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#0D1524] text-[10px] uppercase tracking-wider text-slate-500">
                <tr><th className="px-2 py-1.5 text-left">Marking</th><th className="px-2 py-1.5 text-left">Profil</th><th className="px-2 py-1.5 text-right">Target</th><th className="px-2 py-1.5 text-right">Progress Sebelumnya</th><th className="px-2 py-1.5 text-right">Qty Progress Baru</th></tr>
              </thead>
              <tbody>
                {marks.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-600">Tidak ada marking</td></tr>}
                {marks.map((m) => {
                  const sisa = m.target - m.recorded;
                  return (
                    <tr key={m.id} className={`border-t border-slate-800/70 ${Number(qtys[m.id]) > 0 ? "bg-sky-500/5" : ""}`} data-testid={`pi-row-${m.code}`}>
                      <td className="px-2 py-1 font-mono text-amber-400">{m.code}</td>
                      <td className="px-2 py-1 text-slate-400">{m.profile || "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">{m.target}</td>
                      <td className="px-2 py-1 text-right font-mono text-slate-300" title="Progress sebelumnya (read-only)">{m.recorded}<span className={`ml-1 text-[9px] ${sisa > 0 ? "text-slate-500" : sisa === 0 ? "text-emerald-400" : "text-rose-400"}`}>{sisa > 0 ? `sisa ${sisa}` : sisa === 0 ? "terpenuhi" : `lebih ${-sisa}`}</span></td>
                      <td className="px-2 py-1 text-right"><Input type="number" min="1" step="1" value={qtys[m.id] ?? ""} onChange={(e) => setQtys({ ...qtys, [m.id]: e.target.value })} placeholder="" className="ml-auto h-7 w-24 border-slate-800 bg-slate-950 px-1.5 text-right font-mono text-xs" data-testid={`pi-qty-${m.code}`} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
            <div>
              <div className="mb-1 text-[11px] text-slate-500">PDF bukti progress (1 per batch) *</div>
              <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => { onPdf(e.target.files?.[0]); e.target.value = ""; }} data-testid="pi-pdf-input" />
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={() => pdfRef.current?.click()} className="border-slate-700" data-testid="pi-pdf-button"><Upload className="mr-1.5 h-4 w-4" />{pdf ? "Ganti PDF" : "Upload PDF"}</Button>
                {pdf ? <span className="inline-flex items-center gap-1 text-xs text-emerald-300" data-testid="pi-pdf-name"><FileText className="h-3.5 w-3.5" />{pdfName}<button type="button" onClick={() => { setPdf(""); setPdfName(""); }}><X className="h-3 w-3 text-slate-500" /></button></span> : <span className="text-xs text-slate-500">belum ada</span>}
              </div>
            </div>
            <Field label="Keterangan batch (opsional)"><Input value={ket} onChange={(e) => setKet(e.target.value)} className={sel} data-testid="pi-keterangan" /></Field>
            <div className="flex flex-col justify-end">
              <div className="mb-1 text-right font-mono text-[11px] text-slate-400" data-testid="pi-summary"><b className="text-sky-300">{filled.length}</b> marking · <b className="text-slate-100">{totPcs}</b> pcs · <b className="text-amber-300">{totTon.toFixed(3)}</b> T</div>
              <Button type="button" onClick={() => submit()} disabled={busy || !filled.length} className="bg-sky-600 hover:bg-sky-500" data-testid="pi-save"><Save className="mr-1.5 h-4 w-4" />{busy ? "Menyimpan…" : "Simpan Progress / 保存进度"}</Button>
            </div>
          </div>
        </div>
      )}
      {over && <OverTargetDialog rows={over} notes={overNotes} onNotes={setOverNotes} onNo={() => setOver(null)} onYes={() => { const n = overNotes; setOver(null); submit(n); }} />}
    </div>
  );
}
